import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import MetaDataService from "../services/MetaDataService";
import { useAuth } from "./AuthContext";
import { containerSizeStringToNumber } from "../utils/general";

interface MetadataContextType {
  isMetaDataLoading: boolean;
  containerTypes: Array<{ label: string; value: string }>;
  containerSizes: Array<{ label: string; value: string }>;
  movementCycleTypes: Array<{ label: string; value: string }>;
  jobTypes: Array<{ label: string; value: string }>;
  jobCycles: Array<{ label: string; value: string }>;
  jobStatuses: Array<{ label: string; value: string }>;
  organizationTypes: Array<{ label: string; value: string }>;
  trailerCapacities: Array<{ label: string; value: string }>;
  tripStatuses: Array<{ label: string; value: string }>;
  error: string | null;
}

const MetadataContext = createContext<MetadataContextType | undefined>(
  undefined
);

// Helper function to convert string to title case with spaces
const toTitleCase = (str: string): string => {
  return str
    .toLowerCase() // First convert to lowercase to handle uppercase strings
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

export const MetadataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [isMetaDataLoading, setIsMetaDataLoading] = useState(true);
  const [containerTypes, setContainerTypes] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [containerSizes, setContainerSizes] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [jobCycles, setJobCycles] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [jobTypes, setJobTypes] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [jobStatuses, setJobStatuses] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [organizationTypes, setOrganizationTypes] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [trailerCapacities, setTrailerCapacities] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [tripStatuses, setTripStatuses] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [movementCycleTypes, setMovementCycleTypes] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const fetchMetadata = async () => {
      try {
        const [
          containerSizesResponse,
          containerTypesResponse,
          jobCyclesResponse,
          jobTypesResponse,
          jobStatusesResponse,
          organizationTypesResponse,
          trailerCapacitiesResponse,
          tripStatusesResponse,
        ] = await Promise.all([
          MetaDataService.getContainerSizes(),
          MetaDataService.getContainerTypes(),
          MetaDataService.getJobCycles(),
          MetaDataService.getJobTypes(),
          MetaDataService.getJobStatuses(),
          MetaDataService.getOrgTypes(),
          MetaDataService.getTrailerCapacities(),
          MetaDataService.getTripStatuses(),
        ]);

        // Transform container sizes with proper formatting
        const transformedContainerSizes = containerSizesResponse.data.data.map(
          (item: string) => ({
            label: containerSizeStringToNumber(item),
            value: item,
          })
        );
        setContainerSizes(transformedContainerSizes);

        // Transform container types with title case formatting
        const transformedContainerTypes = containerTypesResponse.data.data.map(
          (item: string) => ({
            label: item,
            value: item,
          })
        );
        setContainerTypes(transformedContainerTypes);

        // Transform job cycles with title case formatting
        const transformedJobCycles = jobCyclesResponse.data.data.map(
          (item: string) => ({
            label: toTitleCase(item),
            value: item,
          })
        );
        setJobCycles(transformedJobCycles);

        // Transform job types with title case formatting
        const transformedJobTypes = jobTypesResponse.data.data.map(
          (item: string) => ({
            label: toTitleCase(item),
            value: item,
          })
        );
        setJobTypes(transformedJobTypes);

        // Transform movement cycle types with title case formatting
        const transformedMovementCycleTypes = jobCyclesResponse.data.data.map(
          (item: string) => ({
            label: toTitleCase(item),
            value: item,
          })
        );
        setMovementCycleTypes(transformedMovementCycleTypes);

        // Transform job statuses with title case formatting
        const transformedJobStatuses = jobStatusesResponse.data.data.map(
          (item: string) => ({
            label: toTitleCase(item),
            value: item,
          })
        );
        setJobStatuses(transformedJobStatuses);

        // Transform organization types with title case formatting
        const transformedOrganizationTypes =
          organizationTypesResponse.data.data.map((item: string) => ({
            label: toTitleCase(item),
            value: item,
          }));
        setOrganizationTypes(transformedOrganizationTypes);

        // Transform trailer capacities with title case formatting
        const transformedTrailerCapacities =
          trailerCapacitiesResponse.data.data.map((item: string) => ({
            label: toTitleCase(item),
            value: item,
          }));
        setTrailerCapacities(transformedTrailerCapacities);

        // Transform trip statuses with title case formatting
        const transformedTripStatuses = tripStatusesResponse.data.data.map(
          (item: string) => ({
            label: toTitleCase(item),
            value: item,
          })
        );
        setTripStatuses(transformedTripStatuses);
        console.log(transformedTripStatuses);
        console.log(transformedTrailerCapacities);
        console.log(transformedJobStatuses);
        console.log(transformedJobTypes);
        console.log(transformedJobCycles);
        console.log(transformedMovementCycleTypes);
        console.log(transformedOrganizationTypes);
        console.log(transformedContainerSizes);
      } catch (err) {
        setError("Failed to fetch metadata");
        console.error("Metadata fetch error:", err);
      } finally {
        setIsMetaDataLoading(false);
      }
    };

    fetchMetadata();
  }, [isAuthenticated]);

  return (
    <MetadataContext.Provider
      value={{
        isMetaDataLoading,
        containerSizes,
        containerTypes,
        jobTypes,
        jobCycles,
        jobStatuses,
        organizationTypes,
        trailerCapacities,
        tripStatuses,
        movementCycleTypes,
        error,
      }}
    >
      {children}
    </MetadataContext.Provider>
  );
};

export const useMetadata = () => {
  const context = useContext(MetadataContext);
  if (!context)
    throw new Error("useMetadata must be used within a MetadataProvider");
  return context;
};
